import { useContext, useEffect, useRef, useState } from "react"
import { Client } from "@xmpp/client"
import { useFeatureFlagEnabled } from "posthog-js/react"
import { Check, ChevronDown } from "lucide-react"
import { useTranslation } from "react-i18next"
import { XmppContext } from "@/stream/xmppClient"
import AdHocCommands, { NaviSelectionInput } from "@/api/adHocCommands"
import { parseNaviJson } from "@/lib/naviParser"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { cn } from "@/lib/utils"
import ContractorSearch, {
  type Contractor,
} from "@/components/maps/ContractorSearch"
type Driver = {
  Id: number
  FirstName?: string
  LastName?: string
  PhoneNumber?: string
}
type Vehicle = {
  Id: number
  RegistrationNumberClean?: string
  // A tractor's set in Marcos: the trailer it is paired with (0/null when
  // none). Absent altogether from a gps that does not send it yet.
  VehicleSetId?: number
  VehicleSet?: string | null
}
type Person = {
  Id: number
  FirstName?: string
  LastName?: string
}

const personName = (p: Person) =>
  `${p.FirstName ?? ""} ${p.LastName ?? ""}`.trim() || `#${p.Id}`

// A vCard's last name field sometimes carries a trailing "(phone)" annotation
// ("Pospieszna (+48727426324)") instead of just the surname. Marcos'
// contains(LastName, query) search then never matches - LastName alone
// doesn't contain that parenthetical - and the supervisor dropdown silently
// falls back to the Slickshift default. Strip it before it ever becomes a
// search query or gets compared against a clean Marcos result.
export const stripParenthetical = (s?: string) =>
  (s || "").replace(/\s*\([^)]*\)\s*$/, "").trim()

const normalizeName = (s?: string) =>
  stripParenthetical(s).toLowerCase().replace(/\s+/g, " ")

// Default-select the logged-in dispatcher among Marcos users. We search by
// last name, but a last name alone can be ambiguous ("Nowak" → 7 users), so we
// only auto-select when the match is UNAMBIGUOUS: a single result, or — when a
// first name is known — exactly one result whose first+last name both match.
// Otherwise we return undefined and leave the field empty (falls back to the
// Slickshift user), because a wrong created_by/forwarder is worse than none.
const pickBestPerson = <T extends Person>(
  results: T[],
  lastName: string | undefined,
  firstName: string | undefined,
): T | undefined => {
  const last = normalizeName(lastName)
  if (!last) return undefined
  const byLast = results.filter((p) => normalizeName(p.LastName) === last)
  if (byLast.length === 1) return byLast[0]
  const first = normalizeName(firstName)
  if (!first) return undefined
  const byBoth = byLast.filter((p) => normalizeName(p.FirstName) === first)
  return byBoth.length === 1 ? byBoth[0] : undefined
}

const label = (c: Contractor) => c.ShortName || c.Name || `#${c.Id}`

const normalizePhone = (p?: string) => (p || "").replace(/\D/g, "")

// Registrations are compared/searched without spaces, dashes or case: our crew
// plates ("PZ 479XN") vs Marcos RegistrationNumberClean ("PZ479XN"). Returns
// undefined when nothing usable remains.
const normalizeRegistration = (reg?: string): string | undefined => {
  const s = (reg || "").replace(/[\s-]/g, "").toUpperCase()
  return s.length > 0 ? s : undefined
}

// Pick the vehicle whose registration matches the crew's plates. Prefers an
// exact normalized match, then a prefix match; otherwise undefined — we never
// guess, an unmatched dropdown stays empty for the dispatcher to fill.
const pickBestVehicle = (
  results: Vehicle[],
  plates: string | undefined,
): Vehicle | undefined => {
  const target = normalizeRegistration(plates)
  if (!target) return undefined
  const regOf = (v: Vehicle) => normalizeRegistration(v.RegistrationNumberClean)
  return (
    results.find((v) => regOf(v) === target) ??
    results.find((v) => {
      const r = regOf(v)
      return r && (r.startsWith(target) || target.startsWith(r))
    })
  )
}

// created_by / forwarder for the Marcos sync API. Constant for now (the
// Slickshift API user in Marcos, id 100000592 → "slickshift"); created_by will
// later follow the logged-in user. The backend also falls back to this id.
const SYNC_USER = {
  id: 100000592,
  firstName: "slickshift",
  lastName: "slickshift",
}

// Fields the order cannot go to Marcos without, in the panel's order, named
// the way their pickers are labelled.
const REQUIRED_FIELD_LABELS = {
  carrier: "orders.naviOrderPanel.carrier.label",
  customer: "orders.naviOrderPanel.customer.label",
  driver: "orders.naviOrderPanel.driver.label",
  truck: "orders.naviOrderPanel.truck.label",
  trailer: "orders.naviOrderPanel.trailer.label",
} as const
type RequiredField = keyof typeof REQUIRED_FIELD_LABELS

// Marcos silently refuses (returns transport id 0, HTTP 200) when the
// transport-level ExternalNumber exceeds 50 chars (verified live; the
// shipment copy tolerates more). The JID local part can be a 36-char UUID
// (`SLICK-<uuid>-<ts>` = 51 → refused), so we cap the whole value at 50: keep
// the `SLICK-` prefix and the base36 timestamp (uniqueness per click) intact,
// and truncate the local part to whatever room is left.
const EXTERNAL_NUMBER_MAX = 50
export const buildExternalNumber = (driverJid: string) => {
  const local = driverJid.split("@")[0]
  const suffix = `-${Date.now().toString(36)}`
  const room = EXTERNAL_NUMBER_MAX - "SLICK-".length - suffix.length
  return `SLICK-${local.slice(0, Math.max(0, room))}${suffix}`
}

// A carrier can have ~1000 drivers — same IQ-overflow problem as contractors.
// Search server-side by name; pre-select the driver matching our phone.
function DriverSearch({
  client,
  vhost,
  driverPhone,
  autoSelectLastName,
  autoSelectFirstName,
  selected,
  onSelect,
  onError,
}: {
  client: Client
  vhost: string
  driverPhone?: string
  // Fallback when Marcos has no phone number on file for this driver (phone
  // match then finds nothing). Same disambiguation as the supervisor
  // dropdowns: only commit an unambiguous match.
  autoSelectLastName?: string
  autoSelectFirstName?: string
  selected?: Driver
  onSelect: (d: Driver | undefined) => void
  onError: () => void
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [text, setText] = useState("")
  const [results, setResults] = useState<Driver[]>([])
  const [loading, setLoading] = useState(false)
  const seq = useRef(0)

  // Pre-select the driver whose phone matches ours. Drivers are not narrowed by
  // carrier — any driver can serve any carrier — so we look up by phone alone.
  // Marcos doesn't always have a phone number on file for a driver, in which
  // case the phone lookup finds nothing to match — fall back to name.
  useEffect(() => {
    onSelect(undefined)
    const phone = normalizePhone(driverPhone)
    const last = stripParenthetical(autoSelectLastName)
    // TEMP debug: entry conditions for the driver auto-select.
    console.log("[navi-debug] DriverSearch effect", {
      driverPhone,
      autoSelectLastName,
      autoSelectFirstName,
      phone,
      last,
    })
    if (!phone && !last) {
      console.log("[navi-debug] DriverSearch skip: no phone or last name")
      return
    }
    let stale = false

    const byName = () => {
      if (!last) {
        console.log("[navi-debug] DriverSearch byName skip: no last name")
        return
      }
      console.log("[navi-debug] getNaviDrivers byName query", { query: last })
      AdHocCommands.getNaviDrivers(client, vhost, { query: last })
        .then((res) => {
          if (stale) return
          const list: Driver[] = parseNaviJson(res)?.drivers || []
          const match = pickBestPerson(
            list,
            autoSelectLastName,
            autoSelectFirstName,
          )
          console.log("[navi-debug] getNaviDrivers byName result", {
            query: last,
            resultCount: list.length,
            results: list,
            matched: match,
          })
          if (match) onSelect(match)
        })
        .catch((e) => {
          console.log("[navi-debug] getNaviDrivers byName error", e)
          onError()
        })
    }

    if (phone) {
      console.log("[navi-debug] getNaviDrivers byPhone query", {
        phone: phone.slice(-9),
      })
      AdHocCommands.getNaviDrivers(client, vhost, {
        phone: phone.slice(-9),
      })
        .then((res) => {
          if (stale) return
          const list: Driver[] = parseNaviJson(res)?.drivers || []
          const match = list.find((d) =>
            normalizePhone(d.PhoneNumber).endsWith(phone.slice(-9)),
          )
          console.log("[navi-debug] getNaviDrivers byPhone result", {
            phone: phone.slice(-9),
            resultCount: list.length,
            results: list,
            matched: match,
          })
          if (match) {
            onSelect(match)
          } else {
            byName()
          }
        })
        .catch((e) => {
          console.log("[navi-debug] getNaviDrivers byPhone error", e)
          onError()
        })
    } else {
      byName()
    }

    return () => {
      stale = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, vhost, driverPhone, autoSelectLastName, autoSelectFirstName])

  useEffect(() => {
    if (!open) return
    const q = text.trim()
    const id = ++seq.current
    setLoading(true)
    const timer = setTimeout(() => {
      AdHocCommands.getNaviDrivers(client, vhost, {
        query: q || undefined,
      })
        .then((res) => {
          if (id !== seq.current) return // ignore stale responses
          setResults(parseNaviJson(res)?.drivers || [])
        })
        .catch(() => onError())
        .finally(() => {
          if (id === seq.current) setLoading(false)
        })
    }, 300)
    return () => clearTimeout(timer)
  }, [client, vhost, text, open, onError])

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-label={t("orders.naviOrderPanel.driver.ariaLabel")}
          aria-expanded={open}
          className="w-full justify-between px-3 font-normal"
        >
          <span
            className={cn("truncate", !selected && "text-muted-foreground")}
          >
            {selected
              ? `${selected.FirstName ?? ""} ${selected.LastName ?? ""}`.trim()
              : t("orders.naviOrderPanel.driver.label")}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="z-[9999] w-[var(--radix-popover-trigger-width)] min-w-[240px] max-w-[90vw] p-0"
        sideOffset={5}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={t("orders.naviOrderPanel.driver.search")}
            value={text}
            onValueChange={setText}
          />
          <CommandList className="max-h-[300px] overflow-y-auto">
            <CommandEmpty>
              {loading
                ? t("orders.naviOrderPanel.searching")
                : t("orders.naviOrderPanel.noResults")}
            </CommandEmpty>
            <CommandGroup>
              {results.map((d) => (
                <CommandItem
                  key={d.Id}
                  value={String(d.Id)}
                  onSelect={() => {
                    onSelect(d)
                    setOpen(false)
                    setText("")
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      selected?.Id === d.Id ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {d.FirstName} {d.LastName}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

// Tractors (Type "V", e.g. TRENAŻER) and trailers (Type "T") both live in the
// Marcos Vehicles collection — hundreds of them, same IQ-overflow problem as
// drivers. Search server-side by registration. Vehicles aren't filtered by
// carrier (any vehicle can serve any carrier), so the whole fleet is searched.
function VehicleSearch({
  client,
  vhost,
  vehicleType,
  placeholder,
  searchPlaceholder,
  ariaLabel,
  selected,
  onSelect,
  onError,
  autoSelectQuery,
}: {
  client: Client
  vhost: string
  vehicleType: "V" | "T"
  placeholder: string
  searchPlaceholder: string
  ariaLabel: string
  selected?: Vehicle
  onSelect: (v: Vehicle | undefined) => void
  onError: () => void
  // Registration from the crew (car plates) to default-select on mount.
  autoSelectQuery?: string
}) {
  const [open, setOpen] = useState(false)
  const [text, setText] = useState("")
  const { t } = useTranslation()
  const [results, setResults] = useState<Vehicle[]>([])
  const [loading, setLoading] = useState(false)
  const seq = useRef(0)

  // Best-effort default selection from the crew's registration. Runs once per
  // query; only fills an empty dropdown and never overrides a manual choice.
  // Marcos stores a cleaned registration ("PZ479XN") while our plates may carry
  // spaces/dashes ("PZ 479XN"), so we search the normalized core and match on
  // the normalized RegistrationNumberClean.
  useEffect(() => {
    if (selected) return
    const q = normalizeRegistration(autoSelectQuery)
    if (!q) return
    let stale = false
    AdHocCommands.getNaviVehicles(client, vhost, vehicleType, { query: q })
      .then((res) => {
        if (stale) return
        const list: Vehicle[] = parseNaviJson(res)?.vehicles || []
        const match = pickBestVehicle(list, autoSelectQuery)
        if (match) onSelect(match)
      })
      .catch(() => onError())
    return () => {
      stale = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, vhost, vehicleType, autoSelectQuery])

  useEffect(() => {
    if (!open) return
    const q = text.trim()
    const id = ++seq.current
    setLoading(true)
    const timer = setTimeout(() => {
      AdHocCommands.getNaviVehicles(client, vhost, vehicleType, {
        query: q || undefined,
      })
        .then((res) => {
          if (id !== seq.current) return // ignore stale responses
          setResults(parseNaviJson(res)?.vehicles || [])
        })
        .catch(() => onError())
        .finally(() => {
          if (id === seq.current) setLoading(false)
        })
    }, 300)
    return () => clearTimeout(timer)
  }, [client, vhost, vehicleType, text, open, onError])

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-label={ariaLabel}
          aria-expanded={open}
          className="w-full justify-between px-3 font-normal"
        >
          <span
            className={cn("truncate", !selected && "text-muted-foreground")}
          >
            {selected
              ? selected.RegistrationNumberClean || `#${selected.Id}`
              : placeholder}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="z-[9999] w-[var(--radix-popover-trigger-width)] min-w-[240px] max-w-[90vw] p-0"
        sideOffset={5}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={text}
            onValueChange={setText}
          />
          <CommandList className="max-h-[300px] overflow-y-auto">
            <CommandEmpty>
              {loading
                ? t("orders.naviOrderPanel.searching")
                : t("orders.naviOrderPanel.noResults")}
            </CommandEmpty>
            <CommandGroup>
              {results.map((v) => (
                <CommandItem
                  key={v.Id}
                  value={String(v.Id)}
                  onSelect={() => {
                    onSelect(v)
                    setOpen(false)
                    setText("")
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      selected?.Id === v.Id ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {v.RegistrationNumberClean || `#${v.Id}`}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

// Office staff (operations / forwarding supervisor) for the created_by /
// forwarder refs. Hundreds of them — search server-side by name, same as
// drivers, but no phone pre-selection: the dispatcher picks explicitly.
function PersonSearch({
  client,
  vhost,
  placeholder,
  searchPlaceholder,
  ariaLabel,
  selected,
  onSelect,
  onError,
  autoSelectLastName,
  autoSelectFirstName,
}: {
  client: Client
  vhost: string
  placeholder: string
  searchPlaceholder: string
  ariaLabel: string
  selected?: Person
  onSelect: (p: Person | undefined) => void
  onError: () => void
  // Logged-in dispatcher's name, to default-select on mount (best-effort).
  autoSelectLastName?: string
  autoSelectFirstName?: string
}) {
  const [open, setOpen] = useState(false)
  const [text, setText] = useState("")
  const { t } = useTranslation()
  const [results, setResults] = useState<Person[]>([])
  const [loading, setLoading] = useState(false)
  const seq = useRef(0)

  // Best-effort default selection from the logged-in dispatcher. Runs once per
  // name; only fills an empty dropdown and never overrides a manual choice.
  // Searches Marcos users by last name and only commits an unambiguous match.
  useEffect(() => {
    // TEMP debug: entry conditions for the supervisor auto-select.
    console.log("[navi-debug] PersonSearch effect", {
      ariaLabel,
      alreadySelected: Boolean(selected),
      autoSelectLastName,
      autoSelectFirstName,
    })
    if (selected) return
    const last = stripParenthetical(autoSelectLastName)
    if (!last) {
      console.log("[navi-debug] PersonSearch skip: no last name", { ariaLabel })
      return
    }
    let stale = false
    console.log("[navi-debug] getNaviPersons query", { ariaLabel, query: last })
    AdHocCommands.getNaviPersons(client, vhost, { query: last })
      .then((res) => {
        if (stale) return
        const list: Person[] = parseNaviJson(res)?.persons || []
        const match = pickBestPerson(
          list,
          autoSelectLastName,
          autoSelectFirstName,
        )
        // TEMP debug: what came back and whether we matched.
        console.log("[navi-debug] getNaviPersons result", {
          ariaLabel,
          query: last,
          resultCount: list.length,
          results: list,
          matched: match,
        })
        if (match) onSelect(match)
      })
      .catch((e) => {
        console.log("[navi-debug] getNaviPersons error", { ariaLabel, e })
        onError()
      })
    return () => {
      stale = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, vhost, autoSelectLastName, autoSelectFirstName])

  useEffect(() => {
    if (!open) return
    const q = text.trim()
    const id = ++seq.current
    setLoading(true)
    const timer = setTimeout(() => {
      AdHocCommands.getNaviPersons(client, vhost, { query: q || undefined })
        .then((res) => {
          if (id !== seq.current) return // ignore stale responses
          setResults(parseNaviJson(res)?.persons || [])
        })
        .catch(() => onError())
        .finally(() => {
          if (id === seq.current) setLoading(false)
        })
    }, 300)
    return () => clearTimeout(timer)
  }, [client, vhost, text, open, onError])

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-label={ariaLabel}
          aria-expanded={open}
          className="w-full justify-between px-3 font-normal"
        >
          <span
            className={cn("truncate", !selected && "text-muted-foreground")}
          >
            {selected ? personName(selected) : placeholder}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="z-[9999] w-[var(--radix-popover-trigger-width)] min-w-[240px] max-w-[90vw] p-0"
        sideOffset={5}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={text}
            onValueChange={setText}
          />
          <CommandList className="max-h-[300px] overflow-y-auto">
            <CommandEmpty>
              {loading
                ? t("orders.naviOrderPanel.searching")
                : t("orders.naviOrderPanel.noResults")}
            </CommandEmpty>
            <CommandGroup>
              {results.map((p) => (
                <CommandItem
                  key={p.Id}
                  value={String(p.Id)}
                  onSelect={() => {
                    onSelect(p)
                    setOpen(false)
                    setText("")
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      selected?.Id === p.Id ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {personName(p)}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export default function NaviOrderPanel({
  vhost,
  driverJid,
  driverPhone,
  driverFirstName,
  driverLastName,
  truckPlate,
  trailerPlate,
  customerName,
  customerNip,
  recipientName,
  recipientNip,
  dispatcherFirstName,
  dispatcherLastName,
  onChange,
}: {
  vhost: string
  driverJid: string
  driverPhone?: string
  // Fallback for the Kierowca auto-select when Marcos has no phone number on
  // file for this driver.
  driverFirstName?: string
  driverLastName?: string
  // Crew's car registration, used to default-select the tractor (Ciągnik).
  truckPlate?: string
  // Trailer plate derived from the tractor→trailer map, used to default-select the trailer (Naczepa).
  trailerPlate?: string
  // Names from the order, used to default-select the dropdowns:
  // customer (Zleceniodawca) and recipient/carrier (Przewoźnik).
  customerName?: string
  // Customer's NIP from the order; tried before customerName (see
  // ContractorSearch's autoSelectNip).
  customerNip?: string
  recipientName?: string
  // Recipient/carrier's NIP; tried before recipientName (see
  // ContractorSearch's autoSelectNip).
  recipientNip?: string
  // Logged-in dispatcher's name, to default-select both supervisor dropdowns.
  dispatcherFirstName?: string
  dispatcherLastName?: string
  onChange: (state: {
    complete: boolean
    selection: NaviSelectionInput
  }) => void
}) {
  const { t } = useTranslation()
  const { client } = useContext(XmppContext)
  // Planners don't assign a tractor/trailer or pick supervisors — those stay
  // with the dispatcher's Marcos flow, so we hide the fields and don't
  // require them for completeness.
  const isPlannerRoleEnabled = Boolean(useFeatureFlagEnabled("planner-role"))
  // Clients who assign the driver and trailer themselves in Marcos (viper):
  // hide both pickers, send neither and don't require them for completeness.
  // The tractor stays.
  const isLimitedMarcos = Boolean(useFeatureFlagEnabled("limited-marcos"))
  const [carrier, setCarrier] = useState<Contractor | undefined>()
  const [customer, setCustomer] = useState<Contractor | undefined>()
  const [driver, setDriver] = useState<Driver | undefined>()
  const [truck, setTruck] = useState<Vehicle | undefined>()
  const [trailer, setTrailer] = useState<Vehicle | undefined>()
  // Operations supervisor → created_by; forwarding supervisor → forwarder.
  const [operationsPerson, setOperationsPerson] = useState<Person | undefined>()
  const [forwardingPerson, setForwardingPerson] = useState<Person | undefined>()
  const [hasError, setHasError] = useState(false)

  const selection = (): NaviSelectionInput => ({
    carrierId: carrier?.Id,
    carrierName: carrier ? label(carrier) : undefined,
    customerId: customer?.Id,
    customerName: customer ? label(customer) : undefined,
    customerNip: customer?.TIN,
    customerAddressId: customer?.HeadquartersAddressId,
    carrierNip: carrier?.TIN,
    carrierAddressId: carrier?.HeadquartersAddressId,
    // created_by (operations) / forwarder (forwarding) supervisors. Default to
    // the constant Slickshift user when the dispatcher picks nobody.
    createdById: operationsPerson?.Id ?? SYNC_USER.id,
    createdByFirstName: operationsPerson?.FirstName ?? SYNC_USER.firstName,
    createdByLastName: operationsPerson?.LastName ?? SYNC_USER.lastName,
    forwarderId: forwardingPerson?.Id ?? SYNC_USER.id,
    forwarderFirstName: forwardingPerson?.FirstName ?? SYNC_USER.firstName,
    forwarderLastName: forwardingPerson?.LastName ?? SYNC_USER.lastName,
    driver1Id: isLimitedMarcos ? undefined : driver?.Id,
    // Marcos accepts trailer/truck only paired with a carrier; the backend
    // drops them otherwise. Send both the registration and the Vehicles Id.
    trailer: isLimitedMarcos ? undefined : trailer?.RegistrationNumberClean,
    trailerId: isLimitedMarcos ? undefined : trailer?.Id,
    truck: truck?.RegistrationNumberClean,
    truckId: truck?.Id,
    externalNumber: buildExternalNumber(driverJid),
  })

  // What still blocks the order, in the panel's order. The "Generate route"
  // button stays disabled until this is empty, so the panel names the fields
  // instead of leaving the dispatcher to guess (oskargroup 2026-11-02: an
  // empty trailer blocked the same order five times in a row).
  const missing = (Object.keys(REQUIRED_FIELD_LABELS) as RequiredField[])
    .filter((field) => {
      if (field === "carrier") return !carrier
      if (field === "customer") return !customer
      if (isPlannerRoleEnabled) return false
      if (field === "truck") return !truck
      if (isLimitedMarcos) return false
      return field === "driver" ? !driver : !trailer
    })
    .map((field) => t(REQUIRED_FIELD_LABELS[field]))
  const complete = missing.length === 0

  // Default the trailer from the tractor's set in Marcos, the pairing the
  // dispatchers keep current. The static tractor→trailer map (trailerPlate)
  // is only the fallback for a gps that does not send the set yet: it misses
  // tractors added since July, and for 175 of its 282 tractors it names a
  // different trailer than their set in Marcos (oskargroup, 2026-11-02). A
  // trailer the dispatcher picked (anything but the last default) survives a
  // change of tractor.
  const defaultTrailerId = useRef<number | undefined>(undefined)
  useEffect(() => {
    if (isLimitedMarcos || !truck || !("VehicleSetId" in truck)) return
    if (trailer && trailer.Id !== defaultTrailerId.current) return
    const fromSet = truck.VehicleSetId
      ? {
          Id: truck.VehicleSetId,
          RegistrationNumberClean: truck.VehicleSet || undefined,
        }
      : undefined
    defaultTrailerId.current = fromSet?.Id
    setTrailer(fromSet)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [truck, isLimitedMarcos])
  const trailerFallbackPlate =
    truck && !("VehicleSetId" in truck) ? trailerPlate : undefined

  // Report selection + completeness to the parent on every change so the
  // single "Generate route" button can validate and create the order. The
  // supervisors are optional (default to SYNC_USER) so they don't gate
  // completeness, but still update the selection when chosen.
  useEffect(() => {
    onChange({ complete, selection: selection() })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    carrier,
    customer,
    driver,
    truck,
    trailer,
    operationsPerson,
    forwardingPerson,
    isLimitedMarcos,
  ])

  if (!client) return null
  const onError = () => setHasError(true)

  return (
    <Card className="mb-4 shadow-sm">
      <CardHeader className="p-4 pb-1 pt-2">
        <h3 className="text-lg font-semibold">Marcos</h3>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 p-4 pt-0">
        <div className="flex flex-col gap-1">
          <Label className="text-xs text-muted-foreground">
            {t("orders.naviOrderPanel.carrier.label")}
          </Label>
          <ContractorSearch
            client={client}
            vhost={vhost}
            carrier={true}
            ariaLabel={t("orders.naviOrderPanel.carrier.ariaLabel")}
            placeholder={t("orders.naviOrderPanel.carrier.label")}
            searchPlaceholder={t("orders.naviOrderPanel.carrier.search")}
            selected={carrier}
            onSelect={(c) => {
              setCarrier(c)
            }}
            onError={onError}
            autoSelectQuery={recipientName}
            autoSelectNip={recipientNip}
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-xs text-muted-foreground">
            {t("orders.naviOrderPanel.customer.label")}
          </Label>
          <ContractorSearch
            client={client}
            vhost={vhost}
            carrier={false}
            ariaLabel={t("orders.naviOrderPanel.customer.ariaLabel")}
            placeholder={t("orders.naviOrderPanel.customer.label")}
            searchPlaceholder={t("orders.naviOrderPanel.customer.search")}
            selected={customer}
            onSelect={setCustomer}
            onError={onError}
            autoSelectQuery={customerName}
            autoSelectNip={customerNip}
          />
        </div>
        {!isPlannerRoleEnabled && (
          <>
            {!isLimitedMarcos && (
              <div className="flex flex-col gap-1">
                <Label className="text-xs text-muted-foreground">
                  {t("orders.naviOrderPanel.driver.label")}
                </Label>
                <DriverSearch
                  client={client}
                  vhost={vhost}
                  driverPhone={driverPhone}
                  autoSelectLastName={driverLastName}
                  autoSelectFirstName={driverFirstName}
                  selected={driver}
                  onSelect={setDriver}
                  onError={onError}
                />
              </div>
            )}
            <div className="flex flex-col gap-1">
              <Label className="text-xs text-muted-foreground">
                {t("orders.naviOrderPanel.truck.label")}
              </Label>
              <VehicleSearch
                client={client}
                vhost={vhost}
                vehicleType="V"
                ariaLabel={t("orders.naviOrderPanel.truck.ariaLabel")}
                placeholder={t("orders.naviOrderPanel.truck.label")}
                searchPlaceholder={t("orders.naviOrderPanel.truck.search")}
                selected={truck}
                onSelect={setTruck}
                onError={onError}
                autoSelectQuery={truckPlate}
              />
            </div>
            {!isLimitedMarcos && (
              <div className="flex flex-col gap-1">
                <Label className="text-xs text-muted-foreground">
                  {t("orders.naviOrderPanel.trailer.label")}
                </Label>
                <VehicleSearch
                  client={client}
                  vhost={vhost}
                  vehicleType="T"
                  ariaLabel={t("orders.naviOrderPanel.trailer.ariaLabel")}
                  placeholder={t("orders.naviOrderPanel.trailer.label")}
                  searchPlaceholder={t("orders.naviOrderPanel.trailer.search")}
                  selected={trailer}
                  onSelect={setTrailer}
                  onError={onError}
                  autoSelectQuery={trailerFallbackPlate}
                />
              </div>
            )}
            <div className="flex flex-col gap-1">
              <Label className="text-xs text-muted-foreground">
                {t("orders.naviOrderPanel.operationsSupervisor.label")}
              </Label>
              <PersonSearch
                client={client}
                vhost={vhost}
                ariaLabel={t(
                  "orders.naviOrderPanel.operationsSupervisor.ariaLabel",
                )}
                placeholder={t(
                  "orders.naviOrderPanel.operationsSupervisor.label",
                )}
                searchPlaceholder={t(
                  "orders.naviOrderPanel.operationsSupervisor.search",
                )}
                selected={operationsPerson}
                onSelect={setOperationsPerson}
                onError={onError}
                autoSelectLastName={dispatcherLastName}
                autoSelectFirstName={dispatcherFirstName}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label className="text-xs text-muted-foreground">
                {t("orders.naviOrderPanel.forwardingSupervisor.label")}
              </Label>
              <PersonSearch
                client={client}
                vhost={vhost}
                ariaLabel={t(
                  "orders.naviOrderPanel.forwardingSupervisor.ariaLabel",
                )}
                placeholder={t(
                  "orders.naviOrderPanel.forwardingSupervisor.label",
                )}
                searchPlaceholder={t(
                  "orders.naviOrderPanel.forwardingSupervisor.search",
                )}
                selected={forwardingPerson}
                onSelect={setForwardingPerson}
                onError={onError}
                autoSelectLastName={dispatcherLastName}
                autoSelectFirstName={dispatcherFirstName}
              />
            </div>
          </>
        )}
        {missing.length > 0 && (
          <div className="text-xs text-amber-700 dark:text-amber-500">
            {t("orders.naviOrderPanel.missingFields", {
              fields: missing.join(", "),
            })}
          </div>
        )}
        {hasError && (
          <div className="text-red-600">
            {t("orders.naviOrderPanel.loadError")}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
